import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import CategorySection from '../components/CategorySection';
import HorizontalProductRow from '../components/HorizontalProductRow';
import VideoFeed from '../components/VideoFeed';
import Hero from '../components/Hero';
import ProductCard from '../components/ProductCard';
import DynamicSectionsRenderer from '../components/DynamicSectionsRenderer';
import LiveHomepageWidget from '../components/LiveHomepageWidget';
import { ShieldCheck, Globe, Headphones, Menu, User, Briefcase, Crown, Flame, Zap, Gift, ChevronLeft, ChevronRight } from 'lucide-react';
import { getTranslation as t, isRTL } from '../utils/translations';
import { useAuth } from '../context/AuthContext';
import { useStorefrontData, sortCategories } from '../hooks/useStorefrontData';
import { resolveSectionProducts, resolveSectionCategories, resolveSectionVideos } from '../utils/sectionResolver';
import { getPersonalizedRecommendations, getServerRecentlyViewed, trackCustomerEvent } from '../api/client';

const RECENTLY_VIEWED_KEY = 'luxvono_recently_viewed_products';



const normalizeSections = (value) => {
    if (!Array.isArray(value)) return [];
    return value
        .filter(section => section?.enabled !== false)
        .sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0));
};

const readRecentlyViewedIds = () => {
    if (typeof window === 'undefined') return [];
    try {
        const raw = JSON.parse(window.localStorage.getItem(RECENTLY_VIEWED_KEY) || '[]');
        return Array.isArray(raw) ? raw.map(Number).filter(Number.isFinite) : [];
    } catch (_) {
        return [];
    }
};

const RealCountdownTimer = ({ endTime }) => {
    const [timeLeft, setTimeLeft] = useState('');
    useEffect(() => {
        if (!endTime) return;
        const target = new Date(endTime).getTime();
        if (isNaN(target)) return;
        const update = () => {
            const diff = target - Date.now();
            if (diff <= 0) {
                setTimeLeft('00h : 00m : 00s');
                return;
            }
            const h = String(Math.floor(diff / (1000 * 60 * 60))).padStart(2, '0');
            const m = String(Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))).padStart(2, '0');
            const s = String(Math.floor((diff % (1000 * 60)) / 1000)).padStart(2, '0');
            setTimeLeft(`${h}h : ${m}m : ${s}s`);
        };
        update();
        const timer = setInterval(update, 1000);
        return () => clearInterval(timer);
    }, [endTime]);
    if (!endTime || !timeLeft) return null;
    return (
        <div className="countdown-widget glass-panel" style={{
            display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', borderRadius: '12px',
            fontSize: '0.875rem', fontWeight: '600', color: 'var(--text)', border: '1px solid rgba(239, 68, 68, 0.2)'
        }}>
            <span style={{ color: '#EF4444' }}>⏳</span>
            <span>{timeLeft}</span>
        </div>
    );
};

const HomePage = ({ onAddToCart, onBuyNow, storeSettings }) => {
    const { isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const [toastMessage, setToastMessage] = useState('');

    const showToast = (msg) => {
        setToastMessage(msg);
        setTimeout(() => setToastMessage(''), 3000);
    };

    const { products, categories, videos, banners, loading } = useStorefrontData(showToast);
    const [serverRecommended, setServerRecommended] = useState([]);
    const [serverRecentlyViewed, setServerRecentlyViewed] = useState([]);

    useEffect(() => {
        let active = true;
        getPersonalizedRecommendations(12)
            .then(res => {
                if (active && Array.isArray(res) && res.length > 0) {
                    setServerRecommended(res);
                }
            })
            .catch(() => {});
        getServerRecentlyViewed(8)
            .then(res => {
                if (active && Array.isArray(res) && res.length > 0) {
                    setServerRecentlyViewed(res);
                }
            })
            .catch(() => {});
        return () => { active = false; };
    }, [user?.id, isAuthenticated]);

    const featuredProducts = useMemo(() => {
        const list = Array.isArray(products) ? products : [];
        if (!list.length) return [];
        const featuredIds = new Set((storeSettings?.featuredProductIds || []).map(Number));
        const explicitlyFeatured = list.filter(item => featuredIds.has(Number(item.id)));
        const remainder = list.filter(item => !featuredIds.has(Number(item.id)));
        return [...explicitlyFeatured, ...remainder].slice(0, 10);
    }, [products, storeSettings?.featuredProductIds]);

    const newArrivalProducts = useMemo(() => {
        const list = Array.isArray(products) ? products : [];
        if (!list.length) return [];
        const sortedNewest = [...list].sort((a, b) => Number(b?.id || 0) - Number(a?.id || 0));
        return sortedNewest.slice(0, 10);
    }, [products]);

    const recommendedProducts = useMemo(() => {
        const list = Array.isArray(products) ? products : [];
        if (!list.length && !serverRecommended.length) return [];
        const merged = [];
        const seen = new Set();
        for (const item of serverRecommended) {
            const pid = Number(item?.id);
            if (pid && !seen.has(pid)) {
                seen.add(pid);
                merged.push(item);
            }
        }
        const excluded = new Set(featuredProducts.slice(0, 4).map(item => Number(item.id)));
        for (const item of list) {
            const pid = Number(item?.id);
            if (pid && !seen.has(pid) && !excluded.has(pid)) {
                seen.add(pid);
                merged.push(item);
            }
        }
        for (const item of list) {
            const pid = Number(item?.id);
            if (pid && !seen.has(pid)) {
                seen.add(pid);
                merged.push(item);
            }
        }
        return merged.slice(0, 12);
    }, [featuredProducts, products, serverRecommended]);

    const flashSaleProducts = useMemo(() => {
        const list = Array.isArray(products) ? products : [];
        if (!list.length) return [];
        const discounted = list.filter(item => Number(item?.compareAtPrice || 0) > Number(item?.price || 0));
        if (discounted.length === 0) return [];
        if (discounted.length >= 8) return discounted.slice(0, 10);
        const discountedIds = new Set(discounted.map(item => Number(item.id)));
        const backfill = list.filter(item => !discountedIds.has(Number(item.id)));
        return [...discounted, ...backfill].slice(0, 10);
    }, [products]);

    const bestSellerProducts = useMemo(() => {
        return [...(products || [])]
            .sort((a, b) => {
                const aSold = Number(a?.soldCount || a?.salesCount || a?.orderCount || 0);
                const bSold = Number(b?.soldCount || b?.salesCount || b?.orderCount || 0);
                if (aSold !== bSold) return bSold - aSold;
                return Number(b?.price || 0) - Number(a?.price || 0);
            })
            .slice(0, 10);
    }, [products]);

    const productsById = useMemo(() => {
        const map = new Map();
        (products || []).forEach(product => map.set(Number(product.id), product));
        return map;
    }, [products]);

    const recentlyViewedProducts = useMemo(() => {
        const merged = [];
        const seen = new Set();
        for (const srvItem of serverRecentlyViewed) {
            const pid = Number(srvItem?.id);
            if (pid && !seen.has(pid)) {
                seen.add(pid);
                merged.push(productsById.get(pid) || srvItem);
            }
        }
        for (const productId of readRecentlyViewedIds()) {
            const pid = Number(productId);
            if (pid && !seen.has(pid)) {
                const found = productsById.get(pid);
                if (found) {
                    seen.add(pid);
                    merged.push(found);
                }
            }
        }
        return merged.slice(0, 8);
    }, [productsById, products, serverRecentlyViewed]);

    const videosById = useMemo(() => {
        const map = new Map();
        (videos || []).forEach(video => map.set(Number(video.id), video));
        return map;
    }, [videos]);

    const productCategoryNamesById = useMemo(() => {
        const map = new Map();
        (products || []).forEach(product => {
            const id = Number(product.id);
            const categoryName = String(product.categoryName || product.category || '').toLowerCase();
            map.set(id, categoryName);
        });
        return map;
    }, [products]);

    const categoryById = useMemo(() => {
        const map = new Map();
        (categories || []).forEach(category => {
            const id = Number(category?.id);
            if (Number.isInteger(id) && id > 0) {
                map.set(id, category);
            }
        });
        return map;
    }, [categories]);

    const categoryCircles = useMemo(() => {
        const allCategories = Array.isArray(categories) ? categories : [];
        const displayLimit = Math.min(30, Math.max(1, Number(storeSettings?.storyCategoriesLimit) || 8));
        const configured = Array.isArray(storeSettings?.storyCategories)
            ? [...storeSettings.storyCategories]
                .sort((a, b) => Number(a?.order || 0) - Number(b?.order || 0))
            : [];

        if (configured.length > 0) {
            const selected = configured
                .filter(item => item?.enabled !== false)
                .map(item => categoryById.get(Number(item?.id)))
                .filter(Boolean);

            if (selected.length >= displayLimit) {
                return selected.slice(0, displayLimit);
            }

            const selectedIds = new Set(selected.map(item => Number(item.id)));
            const extras = sortCategories(allCategories).filter(cat => !selectedIds.has(Number(cat?.id)));
            return [...selected, ...extras].slice(0, displayLimit);
        }

        const homeCategories = allCategories.filter(cat => cat?.showOnHome === true);
        if (homeCategories.length > 0) {
            return sortCategories(homeCategories).slice(0, displayLimit);
        }
        const withProducts = allCategories.filter(cat => Number(cat.productCount || 0) > 0);
        return withProducts.length > 0
            ? sortCategories(withProducts).slice(0, displayLimit)
            : allCategories.slice(0, displayLimit);
    }, [categories, categoryById, storeSettings?.storyCategories, storeSettings?.storyCategoriesLimit]);

    const showHomepageVideoSection = Boolean(storeSettings?.homepageVideoEnabled === true);
    const homepageSections = useMemo(
        () => normalizeSections(storeSettings?.homepageSections || []),
        [storeSettings?.homepageSections]
    );

    const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' ? window.innerWidth < 1024 : false);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 1024);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const activeLang = useMemo(() => {
        try {
            const geoContextStr = localStorage.getItem('luxvono_geo_context');
            const geoContext = geoContextStr ? JSON.parse(geoContextStr) : null;
            return (geoContext && geoContext.language) ? geoContext.language : 'en';
        } catch (_) {
            return 'en';
        }
    }, []);

    const activeCountry = useMemo(() => {
        try {
            const geoContextStr = localStorage.getItem('luxvono_geo_context');
            const geoContext = geoContextStr ? JSON.parse(geoContextStr) : null;
            return (geoContext && geoContext.country) ? geoContext.country : '';
        } catch (_) {
            return '';
        }
    }, []);

    const resolveTitle = (section) => {
        const val = activeLang === 'ar'
            ? (section.titleAr || section.titleEn || section.title || '')
            : (section.titleEn || section.titleAr || section.title || '');
        const upper = String(val || '').trim().toUpperCase();
        if (upper === 'NEW ARRIVALS' || upper === 'NEW CREATIONS') return isRTL() ? 'أحدث الإبداعات' : 'NEW CREATIONS';
        if (upper === 'FLASH SALE') return isRTL() ? 'عروض حصرية سريعة' : 'FLASH SALE';
        if (upper === 'TRENDING' || upper === 'BEST SELLERS') return isRTL() ? 'الأكثر طلباً ورواجاً' : 'TOP TRENDING';
        return t(val);
    };

    const resolveDesc = (section) => {
        const val = activeLang === 'ar'
            ? (section.descAr || section.descEn || section.description || '')
            : (section.descEn || section.descAr || section.description || '');
        const lower = String(val || '').trim().toLowerCase();
        if (!val || lower === 'flash sale sub' || lower.includes('flash sale sub')) {
            return isRTL() ? 'عروض وتخفيضات كبرى لفترة محدودة' : 'Limited-time deals — grab them before they are gone!';
        }
        if (lower === 'new arrivals sub' || lower.includes('new arrivals sub')) {
            return isRTL() ? 'أحدث التشكيلات والمنتجات الفاخرة المضافة حديثاً' : 'Just dropped — fresh additions to our collection';
        }
        return t(val);
    };

    const filteredHomepageSections = useMemo(() => {
        return homepageSections.filter(section => {
            if (section.enabled === false) return false;

            // 1. Scheduling Check
            const now = new Date();
            if (section.publishStart) {
                const start = new Date(section.publishStart);
                if (Number.isFinite(start.getTime()) && now < start) return false;
            }
            if (section.publishEnd) {
                const end = new Date(section.publishEnd);
                if (Number.isFinite(end.getTime()) && now > end) return false;
            }

            // 2. Device Check
            if (section.mobileOnly && !isMobile) return false;
            if (section.desktopOnly && isMobile) return false;

            // 3. Country Check
            if (Array.isArray(section.targetCountries) && section.targetCountries.length > 0) {
                const userCountry = String(activeCountry || '').toUpperCase().trim();
                const matched = section.targetCountries.some(c => String(c || '').toUpperCase().trim() === userCountry);
                if (!matched) return false;
            }

            // 4. Language Check
            if (Array.isArray(section.targetLanguages) && section.targetLanguages.length > 0) {
                const userLang = String(activeLang || '').toLowerCase().trim();
                const matched = section.targetLanguages.some(l => String(l || '').toLowerCase().trim() === userLang);
                if (!matched) return false;
            }

            return true;
        });
    }, [homepageSections, isMobile, activeCountry, activeLang]);

    const hasCustomSections = useMemo(() => {
        return filteredHomepageSections.some(s => s.enabled !== false);
    }, [filteredHomepageSections]);

    const showHero = useMemo(() => {
        const type = String(storeSettings?.heroType || 'slideshow').toLowerCase();
        if (type === 'hidden') return false;
        return true;
    }, [storeSettings?.heroType]);



    const hasVideoSection = filteredHomepageSections.some(sec => sec.type === 'videos' && sec.enabled !== false);

    const renderSkeletonRow = (title, count = 4) => (
        <section className="section container horizontal-product-section">
            <div className="section-header horizontal-product-header">
                <div>
                    <h2 className="section-title horizontal-section-title">{title}</h2>
                    <div className="section-accent horizontal-section-accent" />
                </div>
            </div>
            <div className="horizontal-product-row-scroll">
                {Array.from({ length: count }).map((_, i) => (
                    <div className="horizontal-product-item" key={i}>
                        <div className="product-card glass-panel" style={{ minHeight: '300px' }}>
                            <div className="product-card-img-wrap skeleton skeleton-img" />
                            <div className="product-card-meta" style={{ padding: '16px' }}>
                                <div className="skeleton" style={{ width: '40%', height: '14px', marginBottom: '8px' }} />
                                <div className="skeleton" style={{ width: '80%', height: '18px' }} />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );

    const renderCustomSection = (section, index) => {
        const title = resolveTitle(section);
        const desc = resolveDesc(section);
        const options = { productsById, categoryCircles, featuredProducts, newArrivalProducts, bestSellerProducts, flashSaleProducts, recentlyViewedProducts, recommendedProducts, productCategoryNamesById, videosById };
        const sectionProducts = resolveSectionProducts(section, products, options);
        const sectionCategories = resolveSectionCategories(section, categories, options);
        const sectionVideos = resolveSectionVideos(section, videos, options);

        const containerStyle = {
            paddingTop: section.paddingTop || '0px',
            paddingBottom: section.paddingBottom || '0px',
            borderTopColor: section.customColor || 'var(--lux-line, rgba(255, 255, 255, 0.7))',
            display: 'flex',
            flexDirection: 'column',
            gap: section.gap || '24px'
        };

        const renderSectionHeader = () => {
            if (!title && !desc) return null;
            return (
                <div className="section-header" style={{ marginBottom: '12px' }}>
                    <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {section.iconClass && (
                                <span className="section-icon" style={{ fontSize: '1.25rem', color: section.customColor || 'var(--lux-primary)' }}>
                                    {section.iconClass}
                                </span>
                            )}
                            <h2 className="section-title" style={{ color: section.customColor || 'inherit' }}>{title}</h2>
                        </div>
                        {desc && <p className="section-subtitle">{desc}</p>}
                        <div className="section-accent" style={{ background: section.customColor || 'var(--lux-primary)' }} />
                    </div>
                </div>
            );
        };

        const renderProductsSection = (productsList, forceGrid = false) => {
            if (productsList.length === 0) return null;
            const isGridLayout = forceGrid || section.layoutType === 'grid' || section.type === 'grid-products';
            
            if (isGridLayout) {
                return (
                    <section className="section container" style={containerStyle}>
                        {renderSectionHeader()}
                        <div className="products-grid" style={{
                            display: 'grid',
                            gap: section.gap || '16px'
                        }}>
                            {productsList.map(product => (
                                <ProductCard key={product.id} product={product} onAddToCart={onAddToCart} />
                            ))}
                        </div>
                    </section>
                );
            }

            return (
                <HorizontalProductRow
                    title={title}
                    subtitle={desc}
                    kicker={section.iconClass}
                    products={productsList}
                    rowCount={Math.max(1, Number(section.rowCount || 1))}
                    onAddToCart={onAddToCart}
                />
            );
        };

        switch (section.type) {
            case 'featured-products':
            case 'trending':
            case 'recommended':
            case 'best-sellers':
            case 'new-arrivals':
            case 'recently-viewed':
            case 'ai-recommended':
            case 'custom-manual':
            case 'horizontal-rail':
            case 'products':
            case 'sponsored':
                return (
                    <React.Fragment key={section.id || `custom-${index}`}>
                        <div className="section-separator container" />
                        {renderProductsSection(sectionProducts)}
                    </React.Fragment>
                );

            case 'grid-products':
                return (
                    <React.Fragment key={section.id || `custom-${index}`}>
                        <div className="section-separator container" />
                        {renderProductsSection(sectionProducts, true)}
                    </React.Fragment>
                );

            case 'flash-sale':
                if (sectionProducts.length === 0) return null;
                return (
                    <React.Fragment key={section.id || `custom-${index}`}>
                        <div className="section-separator container" />
                        <section className="section container" style={containerStyle}>
                            <div className="section-header" style={{ marginBottom: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: '16px' }}>
                                    <div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span className="section-icon" style={{ fontSize: '1.25rem', color: '#EF4444' }}>⚡</span>
                                            <h2 className="section-title" style={{ color: '#EF4444' }}>{title || 'FLASH SALE'}</h2>
                                        </div>
                                        {desc && <p className="section-subtitle">{desc}</p>}
                                        <div className="section-accent" style={{ background: '#EF4444' }} />
                                    </div>
                                    {section.timerEndTime ? (
                                        <RealCountdownTimer endTime={section.timerEndTime} />
                                    ) : null}
                                </div>
                            </div>
                            <div className="products-grid" style={{
                                display: 'grid',
                                gap: section.gap || '16px'
                            }}>
                                {sectionProducts.map(product => (
                                    <ProductCard key={product.id} product={product} onAddToCart={onAddToCart} />
                                ))}
                            </div>
                        </section>
                    </React.Fragment>
                );

            case 'categories-slider':
            case 'categories':
                if (sectionCategories.length === 0) return null;
                return (
                    <React.Fragment key={section.id || `custom-${index}`}>
                        <div className="section-separator container" />
                        <section className="section container" style={containerStyle}>
                            {renderSectionHeader()}
                            <CategorySection
                                categories={sectionCategories}
                                highlightedNames={storeSettings?.categoryHighlights || []}
                            />
                        </section>
                    </React.Fragment>
                );

            case 'video-commerce':
            case 'videos':
            case 'video_strip':
                // User requested videos not to be shown on the home screen
                return null;

            case 'banner-section':
            case 'custom-banner':
                if (!section.imageUrl && !section.videoUrl && !title && !desc) return null;
                const linkUrl = section.bannerLink || '/category/all';
                const isLinkExternal = String(linkUrl).startsWith('http');
                const hasVid = !!section.videoUrl;
                
                return (
                    <React.Fragment key={section.id || `custom-${index}`}>
                        <div className="section-separator container" />
                        <section className="container home-custom-banner" style={{
                            ...containerStyle,
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: 'var(--lux-radius-lg, 24px)',
                            minHeight: '280px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                            {hasVid ? (
                                <video
                                    src={section.videoUrl}
                                    autoPlay={false}
                                    muted
                                    loop
                                    playsInline
                                    onMouseEnter={(e) => { e.currentTarget.play().catch(()=>{}); }}
                                    onMouseLeave={(e) => { e.currentTarget.pause(); }}
                                    onTouchStart={(e) => {
                                        if (e.currentTarget.paused) {
                                            e.currentTarget.play().catch(()=>{});
                                        } else {
                                            e.currentTarget.pause();
                                        }
                                    }}
                                    style={{
                                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                        objectFit: 'cover', zIndex: 1
                                    }}
                                />
                            ) : (
                                section.imageUrl && (
                                    <img
                                        src={section.imageUrl}
                                        alt={title || 'Banner'}
                                        loading="lazy"
                                        style={{
                                            position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                                            objectFit: 'cover', zIndex: 1
                                        }}
                                    />
                                )
                            )}
                            <div className="home-custom-banner-overlay" style={{
                                position: 'absolute', inset: 0,
                                background: 'linear-gradient(135deg, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.25) 100%)',
                                zIndex: 2
                            }} />
                            <div className="home-custom-banner-content" style={{
                                position: 'relative', zIndex: 3, textAlign: 'center', color: '#fff',
                                padding: '36px 24px', maxWidth: '720px'
                            }}>
                                {title && <h3 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '12px' }}>{title}</h3>}
                                {desc && <p style={{ fontSize: '1rem', opacity: 0.9, marginBottom: '24px' }}>{desc}</p>}
                                {isLinkExternal ? (
                                    <a href={linkUrl} target="_blank" rel="noreferrer" className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center' }}>
                                        {t('EXPLORE') || 'Explore'}
                                    </a>
                                ) : (
                                    <Link to={linkUrl} className="btn-primary" style={{ display: 'inline-flex', alignItems: 'center' }}>
                                        {t('EXPLORE') || 'Explore'}
                                    </Link>
                                )}
                            </div>
                        </section>
                    </React.Fragment>
                );

            case 'promo-cards':
                return (
                    <React.Fragment key={section.id || `custom-${index}`}>
                        <div className="section-separator container" />
                        <section className="container home-promo-strip" style={{
                            ...containerStyle,
                            display: 'grid',
                            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                            gap: section.gap || '24px'
                        }}>
                            <div className="home-promo-card home-promo-card--primary" style={{ borderColor: section.customColor || 'transparent' }}>
                                <div className="home-promo-icon">{section.iconClass || '🔥'}</div>
                                <h3>{title || t('HOME_TRENDING_TITLE') || 'Trending Now'}</h3>
                                <p>{desc || 'Discover the hottest products everyone is talking about'}</p>
                                <Link to="/category/all" className="home-promo-link">{t('SHOP_NOW') || 'Shop Now'} →</Link>
                            </div>
                            <div className="home-promo-card home-promo-card--accent">
                                <div className="home-promo-icon">⚡</div>
                                <h3>{t('HOME_FLASH_TITLE') || 'Flash Deals'}</h3>
                                <p>{t('HOME_FLASH_DESC') || 'Limited-time offers with up to 70% off select items'}</p>
                                <Link to="/category/all" className="home-promo-link">{t('SHOP_NOW') || 'Shop Now'} →</Link>
                            </div>
                            <div className="home-promo-card home-promo-card--dark">
                                <div className="home-promo-icon">🎁</div>
                                <h3>{t('HOME_FREE_SHIP_TITLE') || 'Free Shipping'}</h3>
                                <p>{t('HOME_FREE_SHIP_DESC') || 'On orders over $50 — worldwide delivery'}</p>
                                <Link to="/category/all" className="home-promo-link">{t('EXPLORE') || 'Explore'} →</Link>
                            </div>
                        </section>
                    </React.Fragment>
                );

            case 'luxury-brands':
                const defaultBrands = [
                    { name: 'Rolex', desc: 'Geneva Horology' },
                    { name: 'Chanel', desc: 'Haute Couture' },
                    { name: 'Gucci', desc: 'Florentine Leather' },
                    { name: 'Prada', desc: 'Milanese Elegance' },
                    { name: 'Cartier', desc: 'Fine Jewelry' },
                    { name: 'Hermès', desc: 'Parisian Heritage' }
                ];
                return (
                    <React.Fragment key={section.id || `custom-${index}`}>
                        <div className="section-separator container" />
                        <section className="section container" style={containerStyle}>
                            {renderSectionHeader()}
                            <div className="brands-grid" style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                                gap: '20px',
                                padding: '10px 0'
                            }}>
                                {defaultBrands.map((brand) => (
                                    <div key={brand.name} className="brand-card glass-panel" style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        padding: '24px', borderRadius: 'var(--lux-radius-md, 16px)', textAlign: 'center',
                                        transition: 'all 0.3s ease', cursor: 'pointer', height: '140px', position: 'relative'
                                    }}>
                                        <div style={{
                                            fontFamily: 'var(--lux-font-family, Outfit)', fontSize: '1.5rem', fontWeight: '700',
                                            letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: '8px', color: 'var(--text)'
                                        }}>
                                            {brand.name}
                                        </div>
                                        <div style={{
                                            fontSize: '0.75rem', color: 'var(--text-muted)', letterSpacing: '0.05em'
                                        }}>
                                            {brand.desc}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    </React.Fragment>
                );

            default:
                return null;
        }
    };

    const fallbackCategories = [
        { id: 'temp-1', name: 'Watches', nameAr: 'ساعات ومجوهرات فاخرة', nameEn: 'Luxury Watches & Jewelry', icon: '⌚' },
        { id: 'temp-7', name: 'Electronics', nameAr: 'إلكترونيات وتقنية ذكية', nameEn: 'Electronics & Smart Tech', icon: '⚡' },
        { id: 'temp-5', name: 'Supercars', nameAr: 'سيارات خارقة ومعدات', nameEn: 'Supercars & Vehicles', icon: '🏎️' },
        { id: 'temp-4', name: 'Apparel', nameAr: 'أزياء وموضة راقية', nameEn: 'Haute Couture Fashion', icon: '👔' },
        { id: 'temp-3', name: 'Leather-Goods', nameAr: 'حقائب وجلود أصلية', nameEn: 'Leather Goods & Bags', icon: '👜' },
        { id: 'temp-10', name: 'Home-Living', nameAr: 'أثاث وديكور فاخر', nameEn: 'Luxury Home & Living', icon: '🛋️' },
        { id: 'temp-8', name: 'Sourcing', nameAr: 'الاستيراد والتوريد الخاص', nameEn: 'Bespoke Sourcing & RFQ', icon: '📦' },
        { id: 'temp-6', name: 'Yachts', nameAr: 'يخوت وملاحة بحرية', nameEn: 'Yachts & Aviation', icon: '🛥️' },
        { id: 'temp-9', name: 'Collectibles', nameAr: 'قطع فنية وتحف نادرة', nameEn: 'Fine Art & Collectibles', icon: '🎨' }
    ];

    const getCatIcon = (name = '', fallbackIcon = '✦') => {
        const lower = String(name).toLowerCase();
        if (lower.includes('watch') || lower.includes('ساع') || lower.includes('مجوهر')) return '⌚';
        if (lower.includes('elect') || lower.includes('شاش') || lower.includes('تقن') || lower.includes('tech') || lower.includes('led')) return '⚡';
        if (lower.includes('car') || lower.includes('سيار')) return '🏎️';
        if (lower.includes('apparel') || lower.includes('fashion') || lower.includes('ملابس') || lower.includes('أزياء')) return '👔';
        if (lower.includes('leather') || lower.includes('bag') || lower.includes('حقائب') || lower.includes('جلد')) return '👜';
        if (lower.includes('home') || lower.includes('furn') || lower.includes('أثاث') || lower.includes('ديكور')) return '🛋️';
        if (lower.includes('sourc') || lower.includes('استيراد')) return '📦';
        if (lower.includes('yacht') || lower.includes('يخوت') || lower.includes('بحر')) return '🛥️';
        return fallbackIcon || '✦';
    };

    const sidebarCategories = useMemo(() => {
        const existingNames = new Set((categories || []).map(c => String(c?.name || '').toLowerCase()));
        const missingFallbacks = fallbackCategories.filter(f => !existingNames.has(String(f.name).toLowerCase()));
        return [...(categories || []), ...missingFallbacks];
    }, [categories]);

    const [heroDealPage, setHeroDealPage] = useState(0);
    const heroDealsList = useMemo(() => {
        const list = Array.isArray(products) && products.length > 0 ? products : [];
        return list.slice(0, 8);
    }, [products]);

    useEffect(() => {
        if (heroDealsList.length >= 4) {
            const timer = setInterval(() => {
                setHeroDealPage(prev => (prev === 0 ? 1 : 0));
            }, 5000);
            return () => clearInterval(timer);
        }
    }, [heroDealsList.length]);

    return (
        <div className="page-home" style={{ position: 'relative' }}>
            {toastMessage && (
                <div style={{
                    position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)',
                    background: '#111111', color: '#ffffff', padding: '12px 28px', borderRadius: '30px',
                    fontWeight: 'bold', zIndex: 99999, boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                    border: '1px solid rgba(255, 255, 255, 0.25)', backdropFilter: 'blur(12px)',
                }}>
                    {toastMessage}
                </div>
            )}
            {!isMobile ? (
                <div className="alibaba-hero-grid container">
                    {/* Column 1: Left Categories Sidebar Menu */}
                    <div className="alibaba-categories-sidebar glass-panel">
                        <div className="sidebar-header">
                            <Menu size={16} />
                            <span>{isRTL() ? 'جميع الفئات والمجموعات' : (t('ALL_CATEGORIES') || 'All Categories')}</span>
                        </div>
                        <ul className="sidebar-list">
                            {sidebarCategories.slice(0, 9).map((cat) => {
                                const catName = (activeLang === 'ar' ? (cat.nameAr || cat.name) : (cat.nameEn || cat.name))?.trim() || cat.name || '';
                                const icon = cat.icon || getCatIcon(cat.name || catName);
                                return (
                                    <li key={cat.id} className="sidebar-item">
                                        <Link to={`/category/${cat.name}`} className="sidebar-link">
                                            <span className="sidebar-icon" style={{ fontSize: '14px' }}>{icon}</span>
                                            <span className="sidebar-name">{catName}</span>
                                        </Link>
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {/* Column 2: Center Spacious Hero Slideshow */}
                    <div className="alibaba-center-hero">
                        <div className="hero-main-slideshow-flex" style={{ width: '100%', height: '100%' }}>
                            {showHero && <Hero storeSettings={storeSettings} banner={banners[0]} />}
                        </div>
                    </div>

                    {/* Column 3: Right Welcoming Panel */}
                    <div className="alibaba-welcome-panel glass-panel">
                        <div className="welcome-avatar-section">
                            <div className="welcome-avatar-ring">
                                {isAuthenticated && user?.profileImageUrl ? (
                                    <img src={user.profileImageUrl} alt="Avatar" className="welcome-avatar-img" />
                                ) : (
                                    <div className="welcome-avatar-placeholder"><User size={24} /></div>
                                )}
                                {isAuthenticated && (
                                    <span className="welcome-avatar-crown-badge">
                                        <Crown size={11} />
                                    </span>
                                )}
                            </div>
                            <h4 className="welcome-heading">
                                {isAuthenticated 
                                    ? `${t('WELCOME') || 'مرحباً'}, ${user?.fullName || user?.firstName || user?.email?.split('@')[0] || 'Customer'}`
                                    : (t('WELCOME_GUEST') || 'مرحباً بك في Luxvono')
                                }
                            </h4>
                            {isAuthenticated ? (
                                <div className="welcome-vip-tier-pill">
                                    <Crown size={11} />
                                    <span>VIP PLATINUM</span>
                                </div>
                            ) : (
                                <p className="welcome-subtext">منصة الاستيراد والتسوق الفاخرة.</p>
                            )}
                            {!isAuthenticated && (
                                <div className="welcome-actions">
                                    <Link to="/login" className="btn-primary welcome-btn">{t('LOGIN') || 'تسجيل الدخول'}</Link>
                                    <Link to="/register" className="btn-secondary welcome-btn">{t('REGISTER') || 'انضم مجاناً'}</Link>
                                </div>
                            )}
                        </div>

                        {/* VIP Coupon Pack Voucher Card (DHgate Global Luxury Style) */}
                        {storeSettings?.welcomeVoucherEnabled !== false && (
                            <div 
                                className={`welcome-vip-voucher voucher-style-${storeSettings?.welcomeVoucherStyle || 'gold-metallic'}`}
                                onClick={() => {
                                    const code = storeSettings?.welcomeVoucherCode || 'LUXVONO50';
                                    const amount = storeSettings?.welcomeVoucherAmount || '50$';
                                    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
                                        navigator.clipboard.writeText(code).catch(() => {});
                                    }
                                    const msg = isRTL() 
                                        ? `🎉 تم تفعيل قسيمة ${amount} ونسخ الكود (${code}) بنجاح!` 
                                        : `🎉 ${amount} voucher claimed & coupon code (${code}) copied!`;
                                    setToastMessage(msg);
                                    setTimeout(() => setToastMessage(''), 4500);

                                    if (storeSettings?.welcomeVoucherLink) {
                                        const link = storeSettings.welcomeVoucherLink.trim();
                                        if (link.startsWith('http')) {
                                            window.open(link, '_blank');
                                        } else {
                                            window.location.href = link;
                                        }
                                    }
                                }}
                                role="button"
                                tabIndex={0}
                                title={isRTL() ? 'انقر لنسخ كود الخصم وتفعيل القسيمة الفاخرة' : 'Click to copy discount code & claim VIP voucher'}
                            >
                                <div className="voucher-ticket-notch-left" />
                                <div className="voucher-ticket-notch-right" />

                                <div className="voucher-top-tier">
                                    <div className="voucher-left">
                                        <span className="voucher-amount">{storeSettings?.welcomeVoucherAmount || '50$'}</span>
                                    </div>
                                    <div className="voucher-divider-perforation" />
                                    <div className="voucher-middle">
                                        <div className="voucher-badge">
                                            {isRTL() 
                                                ? (storeSettings?.welcomeVoucherTitleAr || 'باقة قسائم VIP') 
                                                : (storeSettings?.welcomeVoucherTitleEn || 'VIP COUPON PACK')}
                                        </div>
                                        <div className="voucher-sub">
                                            {isRTL() 
                                                ? (storeSettings?.welcomeVoucherSubtitleAr || 'قسيمة خصم ترحيبية فورية') 
                                                : (storeSettings?.welcomeVoucherSubtitleEn || 'Instant Welcome Discount Voucher')}
                                        </div>
                                    </div>
                                </div>

                                <div className="voucher-bottom-tier">
                                    {storeSettings?.welcomeVoucherCode && (
                                        <div className="voucher-code-pill">
                                            <span className="code-tag">CODE:</span>
                                            <strong>{storeSettings.welcomeVoucherCode}</strong>
                                        </div>
                                    )}
                                    <button type="button" className="voucher-cta-btn">
                                        {isRTL() 
                                            ? (storeSettings?.welcomeVoucherBtnTextAr || 'احصل عليها 🎁') 
                                            : (storeSettings?.welcomeVoucherBtnTextEn || 'GET NOW 🎁')}
                                    </button>
                                </div>
                            </div>
                        )}
                        <div className="welcome-divider" />
                        <div className="welcome-quick-links">
                            <Link to="/sourcing" className="quick-link-item">
                                <span className="quick-link-icon"><Briefcase size={16} /></span>
                                <div className="quick-link-text">
                                    <h5>{t('NAV_SOURCING') || 'الاستيراد الخاص'}</h5>
                                    <p>تقديم طلب RFQ</p>
                                </div>
                            </Link>
                            <Link to="/vip" className="quick-link-item">
                                <span className="quick-link-icon"><Crown size={16} /></span>
                                <div className="quick-link-text">
                                    <h5>{t('NAV_VIP') || 'صالون VIP'}</h5>
                                    <p>خدمات الكونسيرج</p>
                                </div>
                            </Link>
                        </div>
                    </div>
                </div>
            ) : (
                <>
                    {/* Mobile default: render story categories and responsive mobile hero */}
                    {storeSettings?.storyCategoriesEnabled !== false && categoryCircles.length > 0 && (
                        <CategorySection
                            categories={categoryCircles}
                            highlightedNames={storeSettings?.categoryHighlights || []}
                        />
                    )}
                    {showHero && <Hero storeSettings={storeSettings} banner={banners[0]} />}
                </>
            )}

            {/* Slot: top */}
            <DynamicSectionsRenderer
                page="home-page"
                position="top"
                allProducts={products}
                allCategories={categories}
                allVideos={videos}
                onAddToCart={onAddToCart}
                onBuyNow={onBuyNow}
                storeSettings={storeSettings}
            />

            {/* 1. Primary Product Showcase Row (Immediately Below Hero) */}
            {loading && products.length === 0 ? (
                <>
                    <div className="section-separator container" />
                    {renderSkeletonRow(t('NEW_ARRIVALS') || 'NEW CREATIONS')}
                </>
            ) : (
                (newArrivalProducts.length > 0 || products.length > 0) && (
                    <>
                        <div className="section-separator container" style={{ marginTop: '8px', marginBottom: '4px' }} />
                        <HorizontalProductRow
                            title={t('NEW_ARRIVALS')}
                            kicker={t('NEW_PRODUCTS') || "وصل حديثاً"}
                            subtitle={t('NEW_ARRIVALS_SUB') || 'Just dropped — fresh luxury additions to our collection'}
                            products={newArrivalProducts.length > 0 ? newArrivalProducts : (products || []).slice(0, 10)}
                            rowCount={1}
                            onAddToCart={onAddToCart}
                        />
                    </>
                )
            )}

            {/* 2. Luxury Live Stage Showcase */}
            <LiveHomepageWidget onJoinRoom={(roomId) => navigate(`/live/${roomId}`)} />

            {/* 3. Luxury Promo & Trust Strip */}
            <section className="container home-promo-strip" style={{ marginTop: '14px', marginBottom: '14px' }}>
                <div className="home-promo-card home-promo-card--primary">
                    <div className="home-promo-icon"><Flame size={24} /></div>
                    <h3>{t('HOME_TRENDING_TITLE') || 'المقتنيات الأكثر رواجاً'}</h3>
                    <p>{t('HOME_TRENDING_DESC') || 'اكتشف أحدث التشكيلات الفاخرة التي تم انتقاؤها بعناية'}</p>
                    <Link to="/category/all" className="home-promo-link">{t('SHOP_NOW') || 'تسوق الآن'} →</Link>
                </div>
                <div className="home-promo-card home-promo-card--accent">
                    <div className="home-promo-icon"><Zap size={24} /></div>
                    <h3>{t('HOME_FLASH_TITLE') || 'عروض حصرية خاطفة'}</h3>
                    <p>{t('HOME_FLASH_DESC') || 'خصومات استثنائية لفترة محدودة على سلع وماركات مختارة'}</p>
                    <Link to="/category/all" className="home-promo-link">{t('SHOP_NOW') || 'اقتناء الآن'} →</Link>
                </div>
                <div className="home-promo-card home-promo-card--dark">
                    <div className="home-promo-icon"><Gift size={24} /></div>
                    <h3>{t('HOME_FREE_SHIP_TITLE') || 'شحن دولي مؤمن ومجاني'}</h3>
                    <p>{t('HOME_FREE_SHIP_DESC') || 'خدمة التوصيل السريع لجميع أنحاء العالم مع ضمان الأصالة'}</p>
                    <Link to="/category/all" className="home-promo-link">{t('EXPLORE') || 'استكشف الآن'} →</Link>
                </div>
            </section>

            {/* Slot: above-products */}
            <DynamicSectionsRenderer
                page="home-page"
                position="above-products"
                allProducts={products}
                allCategories={categories}
                allVideos={videos}
                onAddToCart={onAddToCart}
                onBuyNow={onBuyNow}
                storeSettings={storeSettings}
            />

            {/* 3. Trending Luxury Row */}
            {(featuredProducts.length > 0 || products.length > 0) && (
                <>
                    <div className="section-separator container" />
                    <HorizontalProductRow
                        title={t('HOME_CURATED') || 'TRENDING LUXURY'}
                        kicker={t('TRENDING') || "رائج حالياً"}
                        subtitle={t('HOME_TRENDING_SUB') || 'تشكيلات حصرية تحظى بإقبال كبار العملاء'}
                        products={featuredProducts.length > 0 ? featuredProducts : (products || []).slice(0, 8)}
                        rowCount={1}
                        onAddToCart={onAddToCart}
                    />
                </>
            )}

            {/* 4. Flash Deals (If Any Discounted) */}
            {flashSaleProducts.length > 0 && (
                <>
                    <div className="section-separator container" />
                    <HorizontalProductRow
                        title={t('FLASH_SALE') || "عروض خاطفة"}
                        kicker={t('URGENCY') || "فرصة ذهبية"}
                        subtitle={t('FLASH_SALE_SUB') || 'عروض وتخفيضات استثنائية لفترة محدودة'}
                        products={flashSaleProducts}
                        rowCount={1}
                        onAddToCart={onAddToCart}
                    />
                </>
            )}

            {/* Slot: between-products */}
            <DynamicSectionsRenderer
                page="home-page"
                position="between-products"
                allProducts={products}
                allCategories={categories}
                allVideos={videos}
                onAddToCart={onAddToCart}
                onBuyNow={onBuyNow}
                storeSettings={storeSettings}
            />

                    {/* 4. Best Sellers */}
                    {bestSellerProducts.length > 0 && (
                        <>
                            <div className="section-separator container" />
                            <HorizontalProductRow
                                title={t('BEST_SELLERS') || "الأكثر مبيعاً"}
                                kicker={t('POPULAR') || "الأكثر طلباً"}
                                subtitle={t('BEST_SELLERS_SUB') || 'Most popular products based on customer orders'}
                                products={bestSellerProducts}
                                rowCount={1}
                                onAddToCart={onAddToCart}
                            />
                        </>
                    )}


                    {/* 6. Complete Product Collection Grid */}
                    {products.length > 0 && (
                        <section className="section container alibaba-products-grid-section" style={{ marginTop: '16px', marginBottom: '24px' }}>
                            <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px' }}>
                                <div>
                                    <span style={{ fontSize: '13px', fontWeight: '700', color: '#e02424', letterSpacing: '1px', textTransform: 'uppercase' }}>
                                        {isRTL() ? 'المجموعة الكاملة' : 'Full Collection'}
                                    </span>
                                    <h2 className="section-title" style={{ fontSize: '24px', fontWeight: '800', margin: '4px 0 0 0' }}>
                                        {isRTL() ? 'استكشف كافة المقتنيات الملكية' : 'Explore All Luxury Collections'}
                                    </h2>
                                </div>
                                <Link to="/category/all" className="btn-secondary" style={{ padding: '8px 18px', borderRadius: '10px', fontSize: '13px', textDecoration: 'none' }}>
                                    {isRTL() ? 'عرض المزيد →' : 'View All →'}
                                </Link>
                            </div>
                            <div className="products-grid alibaba-products-grid-view" style={{ display: 'grid', gap: '16px', width: '100%' }}>
                                {products.map(p => (
                                    <ProductCard
                                        key={p.id}
                                        product={p}
                                        onAddToCart={onAddToCart}
                                        onBuyNow={onBuyNow}
                                    />
                                ))}
                            </div>
                        </section>
                    )}

                    {/* Empty state when catalog has no products yet */}
                    {products.length === 0 && !loading && (
                        <div className="container" style={{ textAlign: 'center', padding: '60px 20px', margin: '30px auto 50px auto', background: '#ffffff', borderRadius: '20px', border: '1px solid #e5e7eb', boxShadow: '0 4px 20px rgba(0,0,0,0.03)', maxWidth: '800px' }}>
                            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: '#f8fafc', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', marginBottom: '14px', border: '1px solid #e2e8f0' }}>✦</div>
                            <h3 style={{ fontSize: '20px', fontWeight: '800', color: '#111827', margin: '0 0 8px 0' }}>
                                {isRTL() ? 'المتجر قيد إعداد المقتنيات الجديدة' : 'Store Collection In Preparation'}
                            </h3>
                            <p style={{ fontSize: '14px', color: '#6b7280', maxWidth: '480px', margin: '0 auto', lineHeight: '1.6' }}>
                                {isRTL() ? 'جاري تدقيق المقتنيات الملكية وتجهيز الكتالوج بالمنتجات الحصرية قريباً.' : 'Exclusive luxury creations are being prepared and will be available in the catalog shortly.'}
                            </p>
                        </div>
                    )}

            {/* Slot: bottom */}
            <DynamicSectionsRenderer
                page="home-page"
                position="bottom"
                allProducts={products}
                allCategories={categories}
                allVideos={videos}
                onAddToCart={onAddToCart}
                onBuyNow={onBuyNow}
                storeSettings={storeSettings}
            />

            {/* Slot: before-footer */}
            <DynamicSectionsRenderer
                page="home-page"
                position="before-footer"
                allProducts={products}
                allCategories={categories}
                allVideos={videos}
                onAddToCart={onAddToCart}
                onBuyNow={onBuyNow}
                storeSettings={storeSettings}
            />

            {/* Monochrome Luxury Seller / Vendor Portal Banner */}
            <section className="container vendor-callout-banner-section" style={{ margin: '35px auto', padding: '0 16px' }}>
                <div style={{
                    background: '#0F1015',
                    borderRadius: '20px',
                    padding: '24px 28px',
                    color: '#FFFFFF',
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '20px',
                    boxShadow: '0 12px 36px rgba(0,0,0,0.18)',
                    border: '1px solid rgba(255, 255, 255, 0.12)',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '18px', zIndex: 1, flex: '1 1 300px' }}>
                        <div style={{
                            width: '56px',
                            height: '56px',
                            borderRadius: '16px',
                            background: '#FFFFFF',
                            color: '#000000',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '28px',
                            flexShrink: 0,
                            boxShadow: '0 4px 16px rgba(255,255,255,0.1)'
                        }}>
                            🏛️
                        </div>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                <span style={{ fontSize: '11px', fontWeight: '800', background: 'rgba(255,255,255,0.1)', color: '#FFFFFF', padding: '2px 8px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', letterSpacing: '0.5px' }}>
                                    {isRTL() ? 'بوابة الموردين والتجار' : 'VENDOR & SELLER PORTAL'}
                                </span>
                                <span style={{ fontSize: '11px', color: '#FFFFFF', opacity: 0.8, fontWeight: '700' }}>● {isRTL() ? 'انضمام فوري' : 'Instant Setup'}</span>
                            </div>
                            <h3 style={{ fontSize: '19px', fontWeight: '800', margin: '0 0 6px 0', color: '#FFFFFF' }}>
                                {isRTL() ? 'هل أنت تاجر أو علامة تجارية فاخرة؟ اعرض منتجاتك الآن' : 'Are you a luxury brand or vendor? Start selling on Luxvono'}
                            </h3>
                            <p style={{ fontSize: '13px', margin: 0, color: 'rgba(255,255,255,0.7)', maxWidth: '580px', lineHeight: '1.5' }}>
                                {isRTL() ? 'انضم لمنظومة التجارة الفاخرة، واستفد من الربط العالمي، والشحن الآمن، ولوحة تحكم متكاملة لإدارة مبيعاتك وأرباحك.' : 'Join the elite luxury commerce ecosystem with worldwide fulfillment and dedicated vendor dashboard analytics.'}
                            </p>
                        </div>
                    </div>
                    <div style={{ zIndex: 1 }}>
                        <Link
                            to="/seller/dashboard"
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '12px 24px',
                                borderRadius: '12px',
                                background: '#FFFFFF',
                                color: '#000000',
                                fontWeight: '800',
                                fontSize: '14px',
                                textDecoration: 'none',
                                boxShadow: '0 4px 15px rgba(255,255,255,0.2)',
                                whiteSpace: 'nowrap',
                                transition: 'transform 0.2s ease'
                            }}
                        >
                            <span>{isRTL() ? 'دخول بوابة البائعين' : 'Enter Seller Portal'}</span>
                            <span style={{ fontSize: '16px' }}>➔</span>
                        </Link>
                    </div>
                </div>
            </section>

            <div className="luxury-trust-strip container" style={{ marginBottom: '60px' }}>
                <span className="trust-pill">
                    <ShieldCheck size={14} />
                    <span>{t('SECURE_CHECKOUT')}</span>
                </span>
                <span className="trust-pill">
                    <Globe size={14} />
                    <span>{t('WORLDWIDE_SHIPPING')}</span>
                </span>
                <span className="trust-pill">
                    <Headphones size={14} />
                    <span>{storeSettings?.trustMessage || t('CONCIERGE_SUPPORT')}</span>
                </span>
            </div>
        </div>
    );
};

export default HomePage;

